import { Icon } from "@sellar-io/components";
import type { ReactNode } from "react";
import { Link, type To } from "react-router";
import { twMerge } from "tailwind-merge";
import { useNavigationOverlayPresentation } from "./NavigationOverlayContext.shared";

type RowProps = {
  children: ReactNode;
  variant?: "option" | "preview" | "identity" | "footer";
  leading?: ReactNode;
  description?: ReactNode;
  trailing?: ReactNode;
  count?: number;
  unread?: boolean;
  selected?: boolean;
  className?: string;
  autoFocus?: boolean;
  titleClassName?: string;
  descriptionClassName?: string;
} & (
  | { to: To; onClick?: () => void; disabled?: never; pending?: never }
  | { to?: never; onClick: () => void; disabled?: boolean; pending?: boolean }
);

export const NavigationOverlayRow = ({
  children,
  variant = "option",
  leading,
  description,
  trailing,
  count,
  unread = false,
  selected = false,
  className,
  autoFocus,
  titleClassName,
  descriptionClassName,
  ...action
}: RowProps) => {
  const isPanel = useNavigationOverlayPresentation() === "panel";
  const isDetailed = variant === "preview" || variant === "identity";
  const optionClasses = isPanel
    ? "min-h-[52px] gap-3 p-4"
    : "min-h-10 gap-2.5 rounded-md p-2.5";
  const classes = twMerge(
    "flex w-full items-center text-left text-sm leading-5 font-medium text-zinc-800 hover:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-purple-600 disabled:cursor-not-allowed disabled:opacity-50",
    isDetailed ? "gap-3 rounded-lg p-3" : optionClasses,
    variant === "preview" && "items-start",
    variant === "footer" &&
      !isPanel &&
      "min-h-5 rounded-none p-0 text-purple-700",
    (selected || unread) && "bg-purple-50 hover:bg-purple-100",
    className,
  );
  const content = (
    <>
      {leading && (
        <span
          className={twMerge(
            "flex shrink-0 items-center justify-center",
            isDetailed ? "size-10" : "size-5",
          )}
        >
          {leading}
        </span>
      )}
      <span
        className={twMerge(
          "min-w-0 flex-1",
          variant === "preview" && "flex flex-col gap-0.5",
        )}
      >
        <span
          className={twMerge(
            "block",
            (unread || selected) && "font-semibold",
            titleClassName,
          )}
        >
          {children}
        </span>
        {description && (
          <span
            className={twMerge(
              "block font-normal text-zinc-500",
              variant === "preview"
                ? "text-sm leading-5"
                : "text-xs leading-[18px]",
              descriptionClassName,
            )}
          >
            {description}
          </span>
        )}
      </span>
      {count !== undefined && (
        <span className="rounded-full bg-purple-600 px-1.5 text-xs text-white">
          {count}
        </span>
      )}
      {selected && (
        <Icon icon="CHECK" size="16px" className="shrink-0 text-purple-600" />
      )}
      {trailing}
    </>
  );

  if (action.to !== undefined) {
    return (
      <Link to={action.to} onClick={action.onClick} className={classes}>
        {content}
      </Link>
    );
  }
  return (
    <button
      type="button"
      autoFocus={autoFocus}
      onClick={action.onClick}
      disabled={action.disabled || action.pending}
      aria-busy={action.pending || undefined}
      aria-pressed={selected || undefined}
      className={classes}
    >
      {content}
    </button>
  );
};
